// Client-side handling for HTTP 429 (rate limited) responses.
//
// When the API rejects a request with 429 we read Retry-After (seconds, or an HTTP date),
// falling back to 60s, and put that action (method + path) on cooldown. Until it expires,
// further calls to the same action are answered locally with a 429 instead of being sent,
// a toast shows the server's message with a live countdown, and components can disable
// their buttons with useRateLimit().
import { useSyncExternalStore } from "react";
import toast from "react-hot-toast";

const FALLBACK_SECONDS = 60;
const TOAST_ID = "rate-limit";
const DEFAULT_MESSAGE = "Too many requests. Please wait and try again.";

// key -> { until: ms timestamp, message }
const cooldowns = new Map();
const listeners = new Set();
let version = 0;
let ticker = null;

// Pages turn a failed request into their own toast.error(err.message). While a rate limit is
// running, the countdown toast above already says it, so those duplicates are dropped
// (a toast.promise loading toast that would have turned into one is dismissed instead).
const RATE_LIMIT_TEXT = /too many requests|rate limit|\b429\b|try again in \d+s/i;
const showError = toast.error;
toast.error = (message, opts) => {
  if (cooldowns.size > 0 && typeof message === "string") {
    const text = message.trim();
    const isDuplicate = RATE_LIMIT_TEXT.test(text) ||
      [...cooldowns.values()].some((c) => text.startsWith(c.message.replace(/[.!?]$/, "")));
    if (isDuplicate) {
      if (opts?.id && opts.id !== TOAST_ID) toast.dismiss(opts.id);
      return opts?.id || TOAST_ID;
    }
  }
  return showError(message, opts);
};

function emit() {
  version += 1;
  listeners.forEach((fn) => fn());
}

function prune(now = Date.now()) {
  let changed = false;
  for (const [key, c] of cooldowns) {
    if (c.until <= now) {
      cooldowns.delete(key);
      changed = true;
    }
  }
  return changed;
}

function secondsLeft(until, now = Date.now()) {
  return Math.max(0, Math.ceil((until - now) / 1000));
}

function renderToast() {
  const now = Date.now();
  let latest = null;
  for (const c of cooldowns.values()) {
    if (!latest || c.until > latest.until) latest = c;
  }
  if (!latest) {
    toast.dismiss(TOAST_ID);
    return;
  }
  const s = secondsLeft(latest.until, now);
  showError(`${latest.message} Try again in ${s}s.`, { id: TOAST_ID, duration: s * 1000 + 1000 });
}

function ensureTicker() {
  if (ticker) return;
  ticker = setInterval(() => {
    prune();
    renderToast();
    emit();
    if (cooldowns.size === 0) {
      clearInterval(ticker);
      ticker = null;
    }
  }, 1000);
}

// "POST /estates/:id/access-logs/check-in". The dev proxy prefix (/api) is dropped so
// proxied and direct calls to the same endpoint share one cooldown.
export function rateLimitKey(input, init) {
  const rawUrl = typeof input === "string" ? input : input?.url || String(input || "");
  const method = String(init?.method || (typeof input === "object" && input?.method) || "GET").toUpperCase();
  let path = rawUrl;
  try {
    path = new URL(rawUrl, window.location.origin).pathname;
  } catch {}
  path = path
    .replace(/^\/api(?=\/)/, "")
    .replace(/\/+$/, "")
    // IDs (ULIDs, GUIDs, numbers) collapse to :id so a limit on one record's endpoint
    // also holds back the same call for every other record.
    .replace(/\/(?:[0-9A-HJKMNP-TV-Z]{26}|[0-9a-f]{8}-[0-9a-f-]{27}|\d+)(?=\/|$)/gi, "/:id");
  return `${method} ${path}`;
}

export function parseRetryAfter(value) {
  if (value === null || value === undefined || value === "") return FALLBACK_SECONDS;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return Math.ceil(n);
  const date = Date.parse(value);
  if (!Number.isNaN(date)) {
    const s = Math.ceil((date - Date.now()) / 1000);
    if (s > 0) return s;
  }
  return FALLBACK_SECONDS;
}

async function readMessage(response) {
  try {
    const text = await response.text();
    const json = JSON.parse(text);
    return json?.message || json?.title || json?.detail || json?.error || DEFAULT_MESSAGE;
  } catch {
    return DEFAULT_MESSAGE;
  }
}

// Call with a 429 response (it is cloned, so the caller can still read the body).
export async function noteRateLimited(key, response) {
  const seconds = parseRetryAfter(response.headers.get("Retry-After"));
  let message = String(await readMessage(response.clone())).trim();
  if (!/[.!?]$/.test(message)) message += ".";
  cooldowns.set(key, { until: Date.now() + seconds * 1000, message });
  renderToast();
  ensureTicker();
  emit();
}

// A local 429 for an action still on cooldown, or null if it may be sent.
export function blockedResponse(key) {
  prune();
  const c = cooldowns.get(key);
  if (!c) return null;
  const s = secondsLeft(c.until);
  renderToast();
  return new Response(JSON.stringify({ succeeded: false, message: `${c.message} Try again in ${s}s.` }), {
    status: 429,
    statusText: "Too Many Requests",
    headers: { "Content-Type": "application/json", "Retry-After": String(s) },
  });
}

function matches(key, match) {
  if (!match) return true;
  const list = Array.isArray(match) ? match : [match];
  return list.some((m) => (m instanceof RegExp ? m.test(key) : key.includes(m)));
}

// Seconds until every action matching `match` (substring, RegExp, or a list of them,
// tested against keys like "POST /auth/forgot-password/start") may be retried; 0 if none.
export function rateLimitRemaining(match) {
  const now = Date.now();
  let max = 0;
  for (const [key, c] of cooldowns) {
    if (matches(key, match)) max = Math.max(max, secondsLeft(c.until, now));
  }
  return max;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Re-renders every second while a matching cooldown is running.
export function useRateLimit(match) {
  useSyncExternalStore(subscribe, () => version);
  return rateLimitRemaining(match);
}
